import React, { useState, useEffect, useRef } from 'react';
import { View, Text, TextInput, TouchableOpacity, ActivityIndicator, ScrollView, Image as RNImage, Alert, StyleSheet, KeyboardAvoidingView, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as TaskManager from 'expo-task-manager';
import * as Location from 'expo-location';
import { Stack, useRouter, useLocalSearchParams } from 'expo-router';
import * as ImagePicker from 'expo-image-picker';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuth } from '@/context/AuthContext';
import { useSaveTrail } from '@/lib/queries/trails';
import VisibilitySelector from '@/components/VisibilitySelector';
import type { Visibility } from '@/types/group';
import styles from './info.styles';
import { openAppSettings } from '@/utils/permissions';
import * as Haptics from 'expo-haptics';
import LottieLoader from '@/components/LottieLoader';
import { useTranslation } from 'react-i18next';
import { loadEndWalkDraft, clearEndWalkDraft } from '@/utils/endWalkDraft';
import { logEvent } from '@/lib/analytics';

export default function InfoPage() {
  const router = useRouter();
  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const insets = useSafeAreaInsets();
  const { session, user } = useAuth();
  const saveTrailMutation = useSaveTrail(session?.accessToken, user?.id);
  const [draft, setDraft] = useState<any>(null);
  const [photos, setPhotos] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<Visibility>('public');
  const [sharedGroupId, setSharedGroupId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const draftId = typeof params?.draftId === 'string' ? params.draftId : undefined;

  useEffect(() => {
    if (!draftId) {
      // Legacy fallback for full draft in URL
      const rawDraft = typeof params?.draft === 'string' ? decodeURIComponent(params.draft as string) : undefined;
      if (!rawDraft) return;
      try {
        const parsed = JSON.parse(rawDraft);
        setDraft(parsed);
        setDescription(parsed?.description || '');
        setPhotos(parsed?.photos || []);
        setVisibility((parsed as any)?.visibility || 'public');
        setSharedGroupId((parsed as any)?.sharedGroupId ?? null);
      } catch {
        // ignore
      }
      return;
    }
    let mounted = true;
    loadEndWalkDraft(draftId).then((parsed) => {
      if (!mounted || !parsed) return;
      setDraft(parsed);
      setDescription(parsed?.description || '');
      setPhotos(parsed?.photos || []);
      setVisibility((parsed as any)?.visibility || 'public');
      setSharedGroupId((parsed as any)?.sharedGroupId ?? null);
    });
    return () => { mounted = false; };
  }, [draftId, params?.draft]);

  const pickImages = async () => {
    const { status: existingStatus } = await ImagePicker.getMediaLibraryPermissionsAsync();

    if (existingStatus === 'denied') {
      Alert.alert(
        t('endWalkInfo.photoLibraryAccessNeeded'),
        t('endWalkInfo.photoLibraryMessage'),
        [
          { text: t('common.cancel'), style: 'cancel' },
          { text: t('record.openSettings'), onPress: openAppSettings },
        ],
      );
      return;
    }

    if (existingStatus !== 'granted') {
      await new Promise<void>((resolve) => {
        Alert.alert(
          t('endWalkInfo.allowPhotoAccess'),
          t('endWalkInfo.allowPhotoAccessMessage'),
          [{ text: t('common.continue'), onPress: () => resolve() }],
          { cancelable: false },
        );
      });

      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert(
          t('endWalkInfo.photoLibraryAccessNeeded'),
          t('endWalkInfo.photoLibraryMessage'),
          [
            { text: t('common.cancel'), style: 'cancel' },
            { text: t('record.openSettings'), onPress: openAppSettings },
          ],
        );
        return;
      }
    }

    try {
      const result = await ImagePicker.launchImageLibraryAsync({
        allowsMultipleSelection: true,
        quality: 0.6,
        mediaTypes: ['images'],
        // ponytail: iOS PHPicker fails to load HEIC originals (esp. iCloud) — force JPEG
        preferredAssetRepresentationMode: ImagePicker.UIImagePickerPreferredAssetRepresentationMode.Compatible,
      });
      if (!result.canceled) {
        const uris = (result.assets || []).map(a => a.uri);
        setPhotos(prev => [...prev, ...uris]);
      }
    } catch {
      Alert.alert(t('common.error'), t('trailEdit.failedToPickImages'));
    }
  };

  const handleOpenSharePopup = () => {
    if (true) Haptics.selectionAsync();
    router.push(`/end-walk/share?draftId=${draftId}` as any);
  };




  const onBack = () => router.back();

  const onDone = async () => {
    if (!draft) return router.push('/');
    setSaving(true);
    try {
      const { name: _unusedName, ...draftWithoutName } = draft || {};
      const toSave = {
        ...draftWithoutName,
        description: description || draft.description,
        photos,
      };
      await saveTrailMutation.mutateAsync({ trail: toSave, visibility, sharedGroupId });
      logEvent('walk_completed', { distance: toSave.distance, duration: toSave.duration, flow: 'record' });
      // Clear any recording state so the Record tab stops resuming recording
      try {
        const hasTask = await TaskManager.isTaskRegisteredAsync('background-location-task');
        if (hasTask) {
          await Location.stopLocationUpdatesAsync('background-location-task');
        }
      } catch {
        // ignore
      }
      try {
        await AsyncStorage.removeItem('recording_start_time');
        await AsyncStorage.removeItem('recording_coordinates');
        await AsyncStorage.removeItem('recording_max_elevation');
        await AsyncStorage.removeItem('recording_max_speed');
        await AsyncStorage.removeItem('recording_last_update');
      } catch {
        // ignore
      }
      // Clear the draft so stale data doesn't linger in AsyncStorage
      clearEndWalkDraft(draftId);
      router.replace('/');
    } catch (e) {
      console.warn('save failed', e);
      logEvent('walk_save_failed', { flow: 'record' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <View style={[styles.container, { paddingTop: insets.top + 20 || 20 }]}>
        {/* Keep Back/Share/Done reachable above the keyboard when the content is tall */}
        <KeyboardAvoidingView style={{ flex: 1 }} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView contentContainerStyle={styles.infoScroll} keyboardShouldPersistTaps="handled">
          <View style={styles.moreCard}>
            <Text style={styles.cardTitle}>{t('endWalkInfo.addPhotos')}</Text>
            <Text style={styles.cardSub}>{t('endWalkInfo.showHighlights')}</Text>

            <TouchableOpacity style={styles.addPhotoBox} onPress={pickImages}>
              <Text style={styles.plusSign}>+</Text>
            </TouchableOpacity>

            <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.photoRow}>
              {photos.map((p, i) => {
                const extraStyle: any = { zIndex: i };
                if (i === 1) extraStyle.transform = [{ rotate: '-6deg' }, { translateY: 6 }];
                if (i === 2) extraStyle.transform = [{ rotate: '-3deg' }, { translateY: 3 }];
                return <RNImage key={i} source={{ uri: p }} style={[styles.thumb, extraStyle]} />;
              })}
            </ScrollView>

            <Text style={styles.cardTitle}>{t('endWalkInfo.anythingToShare')}</Text>
            <Text style={styles.cardSub}>{t('endWalkInfo.shareHelpfulInfo')}</Text>
            <TextInput
              placeholder={t('endWalkInfo.anythingElse')}
              placeholderTextColor="rgba(255,255,255,0.25)"
              value={description}
              onChangeText={setDescription}
              style={styles.notesInput}
              multiline
            />

            {/* Audience selector */}
            <View style={{ marginTop: 18 }}>
              <Text style={styles.cardTitle}>{t('visibility.audience')}</Text>
              <Text style={styles.cardSub}>{t('visibility.whoCanSee')}</Text>
              <VisibilitySelector
                token={session?.accessToken}
                visibility={visibility}
                sharedGroupId={sharedGroupId}
                onChange={(v, g) => { setVisibility(v); setSharedGroupId(g ?? null); }}
              />
            </View>
          </View>

          <View style={{ height: 24 }} />

          <View style={styles.bottomRow}>
            <TouchableOpacity style={styles.backButton} onPress={onBack}>
              <Text style={styles.backText}>{t('common.back')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.shareButton, saving && styles.shareButtonDisabled]}
              onPress={handleOpenSharePopup}
              disabled={saving}
            >
              <Text style={styles.shareButtonText}>{t('endWalkInfo.share')}</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.doneButton, saving && styles.doneButtonDisabled]}
              onPress={onDone}
              disabled={saving}
            >
              {saving ? <ActivityIndicator color="#000" /> : <Text style={styles.doneText}>{t('common.done')}</Text>}
            </TouchableOpacity>
          </View>
        </ScrollView>
        </KeyboardAvoidingView>
      </View>

      {/* Share Popup */}
      {/* Full-screen blocking loader while uploading */}
      {saving && (
        <View style={StyleSheet.absoluteFill} pointerEvents="auto">
          <View style={styles.savingOverlay}>
            <LottieLoader size={120} />
            <Text style={styles.savingText}>{t('common.saving')}</Text>
          </View>
        </View>
      )}
    </>
  );
}
